import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy, doc, deleteDoc } from "firebase/firestore";
import { Search, Plus, Edit2, Trash2, ChevronRight } from 'lucide-react';
import Spinner from './Spinner.js';
import { useNavigate } from 'react-router-dom';

const AdminProducts = ({ db, user }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!user) {
      setProducts([]);
      setLoading(false);
      return;
    }

    // Subscribe to the consolidated products collection.
    const q = query(collection(db, "products"), orderBy("sku", "asc"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(list);
        setLoadError('');
        setLoading(false);
      },
      (error) => {
        console.error("Error loading products:", error);
        setProducts([]);
        setLoadError(
          error?.code === 'permission-denied'
            ? 'You do not have permission to read the product catalog. Please check Firestore rules.'
            : `Unable to load products: ${error.message}`
        );
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, user]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch =
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = filterType === 'All' || p.type === filterType;

      return matchesSearch && matchesType;
    });
  }, [products, searchTerm, filterType]);

  const productTypes = useMemo(() => {
    const types = new Set(products.map(p => p.type).filter(Boolean));
    return ['All', ...Array.from(types)];
  }, [products]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product from the master catalog?")) {
      try {
        await deleteDoc(doc(db, "products", id));
      } catch (error) {
        alert("Error deleting: " + error.message);
      }
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Product Catalog</h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '5px 0 0 0' }}>Manage master data, pricing and attributes for all SKUs</p>
        </div>
        <button
          onClick={() => navigate('/admin/products/new')}
          style={addBtnStyle}
        >
          <Plus size={18} /> Add New Product
        </button>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1, minWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search SKU or Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={inputStyle}
          />
        </div>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={selectStyle}
        >
          {productTypes.map(t => <option key={t} value={t}>{t === 'All' ? 'All Types' : t}</option>)}
        </select>
      </div>

      {/* TABLE */}
      <div style={tableContainerStyle}>
        {loadError ? (
          <div style={{ padding: '16px 20px', backgroundColor: '#fff7ed', color: '#9a3412', borderBottom: '1px solid #fed7aa', fontSize: '14px' }}>
            {loadError}
          </div>
        ) : null}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={theadStyle}>
              <th style={thStyle}>SKU & Type</th>
              <th style={thStyle}>Item Name</th>
              <th style={thStyle}>UOM</th>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Rates (S/R)</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No products found in catalog.</td>
              </tr>
            ) : (
              filteredProducts.map((p) => (
                <tr
                  key={p.id}
                  style={{ ...trStyle, cursor: 'pointer' }}
                  className="hover-highlight"
                  onClick={() => navigate(`/admin/products/${p.id}`)}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{p.sku}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>{p.type || '---'}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '500' }}>{p.name}</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>{p.brand || 'Nexrise'}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={badgeStyle}>{p.uom || 'nos'}</span>
                  </td>
                  <td style={tdStyle}>{p.category || '---'}</td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: '13px' }}>S: ₹{p.rate || 0}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>R: ₹{p.rentalRate || 0}</div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/admin/products/${p.id}`)}
                        style={actionBtnStyle} title="View Details"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/products/edit/${p.id}`)}
                        style={actionBtnStyle} title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        style={{ ...actionBtnStyle, color: '#ef4444' }} title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const filterBarStyle = {
  display: 'flex', gap: '15px', marginBottom: '20px', backgroundColor: 'white',
  padding: '15px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', flexWrap: 'wrap'
};

const inputStyle = {
  width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px',
  border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px'
};

const selectStyle = {
  padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', minWidth: '150px'
};

const tableContainerStyle = {
  backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden'
};

const theadStyle = {
  backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0'
};

const thStyle = {
  padding: '15px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700',
  color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em'
};

const tdStyle = {
  padding: '15px 20px', fontSize: '14px', verticalAlign: 'middle'
};

const trStyle = {
  borderBottom: '1px solid #f1f5f9'
};

const badgeStyle = {
  backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '4px',
  fontSize: '11px', color: '#475569', fontWeight: 'bold', textTransform: 'uppercase'
};

const actionBtnStyle = {
  padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0',
  backgroundColor: 'white', color: '#64748b', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center'
};

const addBtnStyle = {
  display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
  borderRadius: '8px', border: 'none', backgroundColor: '#2563eb',
  color: 'white', fontWeight: '600', cursor: 'pointer'
};

export default AdminProducts;
